import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CalendarPlus, Mail, Phone } from 'lucide-react'
import { toast } from 'sonner'

import { FollowupFormDialog, type FollowupFormState } from '@/components/sales/followup-form-dialog'
import { FollowupTree } from '@/components/sales/followup-tree'
import { EnquiryStageSelect } from '@/components/sales/enquiry-stage-select'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getErrorMessage } from '@/lib/api'
import { getQuotationQuickActionClass } from '@/lib/quotation-sales'
import { buildFollowupTree, formatSalesFollowupDate } from '@/lib/sales-followups'
import type { EnquiryStage } from '@/lib/sales-enquiry-stages'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import {
  tenantInputToIso,
  tenantInputToIsoKeepingSource,
  toTenantInputValue,
  useRequiredTenantTimeZone,
} from '@/lib/tenant-date-time'
import { salesApi, type ContactEnquiry, type CreateFollowupPayload, type SalesFollowupTask } from '@/services/sales'

const EMPTY_FORM: FollowupFormState = {
  title: '',
  activity_type: 'general',
  priority: 'medium',
  due_at: '',
  assignee_user_id: null,
  assignee_name: '',
  assignee_email: '',
  notes: '',
}

export default function EnquiryFollowupsPage() {
  const { enquiryId } = useParams()
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  // One tenant timezone for edit seed, submit and display of due_at; never the browser zone.
  const { timeZone: tenantTimeZone, isReady: isTenantTimeZoneReady } = useRequiredTenantTimeZone(serviceInstanceId)
  const queryClient = useQueryClient()
  const parsedEnquiryId = Number(enquiryId)

  const [formMode, setFormMode] = useState<'create-root' | 'create-child' | 'edit' | null>(null)
  const [activeTask, setActiveTask] = useState<SalesFollowupTask | null>(null)
  const [form, setForm] = useState<FollowupFormState>(EMPTY_FORM)
  const [formError, setFormError] = useState('')
  const [completingId, setCompletingId] = useState<number | null>(null)

  // An edit opened before the tenant timezone was known has an empty due_at; fill it once it is.
  useEffect(() => {
    if (!tenantTimeZone) {
      return
    }
    setForm((current) => (formMode === 'edit' && !current.due_at && activeTask?.due_at
      ? { ...current, due_at: toTenantInputValue(activeTask.due_at, tenantTimeZone) }
      : current))
    // Only when the timezone becomes available; later edits by the user are kept.
  }, [tenantTimeZone])

  const enquiriesQuery = useQuery({
    queryKey: ['sales', 'enquiries', serviceInstanceId],
    queryFn: () => salesApi.listEnquiries({ service_instance_id: serviceInstanceId }),
    enabled: Number.isFinite(parsedEnquiryId),
  })

  const followupsQuery = useQuery({
    queryKey: ['sales', 'followups', 'enquiry', parsedEnquiryId, serviceInstanceId],
    queryFn: () => salesApi.listFollowups({
      service_instance_id: serviceInstanceId,
      enquiry_id: parsedEnquiryId,
    }),
    enabled: Number.isFinite(parsedEnquiryId),
  })

  const enquiry = useMemo(() => {
    return (enquiriesQuery.data?.results ?? []).find((item) => item.id === parsedEnquiryId) ?? null
  }, [enquiriesQuery.data, parsedEnquiryId])

  const followupTree = useMemo(
    () => buildFollowupTree(followupsQuery.data?.results ?? []),
    [followupsQuery.data],
  )

  const rootFollowup = followupTree[0] ?? null

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sales', 'followups'] })
    queryClient.invalidateQueries({ queryKey: ['sales', 'enquiries', serviceInstanceId] })
  }

  const updateStageMutation = useMutation({
    mutationFn: ({ id, stage }: { id: number; stage: EnquiryStage }) => salesApi.updateEnquiryStage(id, stage),
    onSuccess: () => {
      invalidate()
      toast.success('Enquiry stage updated')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const createMutation = useMutation({
    mutationFn: (payload: CreateFollowupPayload) => salesApi.createFollowup(payload),
    onSuccess: () => {
      invalidate()
      setFormMode(null)
      setActiveTask(null)
      setForm(EMPTY_FORM)
      setFormError('')
      toast.success('Follow-up saved')
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload, storedDueAt }: { id: number; payload: FollowupFormState; storedDueAt?: string | null }) => salesApi.updateFollowup(id, {
      ...payload,
      // An unedited due date is sent back as the stored instant.
      due_at: tenantInputToIsoKeepingSource(payload.due_at, tenantTimeZone, storedDueAt) ?? null,
    }),
    onSuccess: () => {
      invalidate()
      setFormMode(null)
      setActiveTask(null)
      setForm(EMPTY_FORM)
      setFormError('')
      toast.success('Follow-up updated')
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const completeMutation = useMutation({
    mutationFn: (id: number) => salesApi.completeFollowup(id),
    onMutate: (id) => setCompletingId(id),
    onSuccess: () => {
      invalidate()
      toast.success('Follow-up completed')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
    onSettled: () => setCompletingId(null),
  })

  const openCreateRoot = (enq: ContactEnquiry) => {
    setFormMode('create-root')
    setActiveTask(null)
    setForm({
      ...EMPTY_FORM,
      title: `Follow up: ${enq.name}`,
      activity_type: 'call',
      notes: enq.message ? `Re: ${enq.message}` : '',
    })
    setFormError('')
  }

  const openCreateChild = (task: SalesFollowupTask) => {
    setFormMode('create-child')
    setActiveTask(task)
    setForm({
      ...EMPTY_FORM,
      title: `Next follow-up: ${task.title}`,
      activity_type: task.activity_type,
      priority: task.priority,
      assignee_user_id: task.assignee_user_id,
      assignee_name: task.assignee_name,
      assignee_email: task.assignee_email,
      notes: task.notes,
    })
    setFormError('')
  }

  const openEdit = (task: SalesFollowupTask) => {
    if (task.is_editable === false) {
      toast.error('Parent follow-ups cannot be edited after child follow-ups are created.')
      return
    }
    setFormMode('edit')
    setActiveTask(task)
    setForm({
      title: task.title,
      activity_type: task.activity_type,
      priority: task.priority,
      due_at: toTenantInputValue(task.due_at, tenantTimeZone),
      assignee_user_id: task.assignee_user_id,
      assignee_name: task.assignee_name,
      assignee_email: task.assignee_email,
      notes: task.notes,
    })
    setFormError('')
  }

  const handleSubmit = () => {
    if (!form.title.trim()) {
      setFormError('Title is required.')
      return
    }
    if (!isTenantTimeZoneReady) {
      return
    }

    if (formMode === 'edit' && activeTask) {
      updateMutation.mutate({ id: activeTask.id, payload: form, storedDueAt: activeTask.due_at })
      return
    }

    const payload: CreateFollowupPayload = {
      ...form,
      service_instance_id: serviceInstanceId,
      enquiry_id: parsedEnquiryId,
      due_at: tenantInputToIso(form.due_at, tenantTimeZone) ?? null,
      parent_followup_id: formMode === 'create-child' ? activeTask?.id ?? null : null,
    }
    createMutation.mutate(payload)
  }

  if (!Number.isFinite(parsedEnquiryId)) {
    return <div className="p-6 text-sm text-muted-foreground">Invalid enquiry.</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <Button variant="ghost" size="sm" className="-ml-2 w-fit px-2" asChild>
            <Link to={withServiceInstanceId('/sales/enquiries', serviceInstanceId)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to enquiries
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Enquiry Follow-ups</h1>
            <p className="text-sm text-muted-foreground">Review the full follow-up chain for this enquiry.</p>
          </div>
        </div>
        {!rootFollowup && enquiry ? (
          <Button variant="outline" className={getQuotationQuickActionClass('open')} onClick={() => openCreateRoot(enquiry)}>
            <CalendarPlus className="mr-2 h-4 w-4" />
            Add Follow-up
          </Button>
        ) : null}
      </div>

      {enquiry ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              {enquiry.name}
              <Badge variant={enquiry.is_read ? 'secondary' : 'outline'}>{enquiry.is_read ? 'Read' : 'Unread'}</Badge>
              <EnquiryStageSelect
                value={enquiry.stage || 'new'}
                disabled={updateStageMutation.isPending}
                onChange={(stage) => updateStageMutation.mutate({ id: enquiry.id, stage })}
              />
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-2">
            <div className="space-y-1">
              {enquiry.email ? <div className="flex items-center gap-2"><Mail className="h-4 w-4" />{enquiry.email}</div> : null}
              {enquiry.phone ? <div className="flex items-center gap-2"><Phone className="h-4 w-4" />{enquiry.phone}</div> : null}
              <div className="text-muted-foreground">Received {formatSalesFollowupDate(enquiry.created_at, tenantTimeZone)}</div>
              {enquiry.stage_updated_at ? (
                <div className="text-muted-foreground">Stage updated {formatSalesFollowupDate(enquiry.stage_updated_at, tenantTimeZone)}</div>
              ) : null}
            </div>
            <div className="rounded-lg border bg-muted/20 px-3 py-2">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Enquiry message</div>
              <div className="whitespace-pre-wrap">{enquiry.message || '-'}</div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Follow-up hierarchy</CardTitle>
        </CardHeader>
        <CardContent>
          {followupsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading follow-ups...</p>
          ) : (
            <FollowupTree
              nodes={followupTree}
              serviceInstanceId={serviceInstanceId}
              completingId={completingId}
              onComplete={(id) => completeMutation.mutate(id)}
              onEdit={openEdit}
              onAddChild={openCreateChild}
              timeZone={tenantTimeZone}
            />
          )}
        </CardContent>
      </Card>

      <FollowupFormDialog
        open={formMode !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFormMode(null)
            setActiveTask(null)
            setFormError('')
          }
        }}
        title={
          formMode === 'edit'
            ? 'Edit Follow-up'
            : formMode === 'create-child'
              ? 'Add Child Follow-up'
              : 'Add Follow-up'
        }
        subtitle={enquiry?.name ? `for ${enquiry.name}` : undefined}
        serviceInstanceId={serviceInstanceId}
        form={form}
        onFormChange={setForm}
        error={formError}
        submitting={createMutation.isPending || updateMutation.isPending}
        submitLabel={formMode === 'edit' ? 'Save Changes' : 'Add Follow-up'}
        submitDisabled={!isTenantTimeZoneReady}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
